/**
 * SSMade Product Configurator
 * Vanilla JS — no dependencies
 */
(function () {
  'use strict';

  const PRICES = {
    base: 169,
    arch: 20,
    moss: 15,
    nfc: 45,
    nameplates: 18,
    nfc_keychain: 25,
  };

  const FRAME_COLORS = {
    black: '#1C1C1C',
    white: '#F0EDE8',
    natural: '#C8BC9E',
  };

  const FRAME_BORDERS = {
    black: '#2E2E2E',
    white: '#CFC9C2',
    natural: '#A89A7E',
  };

  const MAG_BORDER_COLOR = 'rgba(200, 169, 110, 0.4)';
  const MAG_TEXT_COLOR = 'rgba(200, 169, 110, 0.65)';

  const FONT_MAP = {
    'clean-sans': "'Helvetica Neue', Helvetica, Arial, sans-serif",
    'elegant-serif': "Georgia, 'Times New Roman', serif",
    'script': "cursive",
  };

  const TEXT_COLORS = [
    '#FFFFFF', '#0E0E0E', '#C8A96E', '#F7F5F1',
    '#3A3A3A', '#8B0000', '#1B4332', '#1E3A5F',
  ];

  class SSMadeConfigurator {
    constructor(container) {
      if (!container) return;
      this.container = container;
      this.canvas = container.querySelector('#ssmade-preview-canvas');
      if (!this.canvas) return;

      this.ctx = this.canvas.getContext('2d');
      this.dpr = window.devicePixelRatio || 1;
      this.CW = 460;
      this.CH = 540;
      this.nextId = 1;

      this.state = {
        frameStyle: 'standard',
        frameColor: 'black',
        addons: new Set(),
        blocks: [],
        selected: null,
        dragging: false,
        dragOX: 0,
        dragOY: 0,
      };

      this._init();
    }

    _init() {
      this._setupCanvas();
      this._buildTextColorSwatches();
      this._bindControls();
      this._bindCanvas();
      this._render();
      this._updatePrice();
    }

    _setupCanvas() {
      this.canvas.width = this.CW * this.dpr;
      this.canvas.height = this.CH * this.dpr;
      this.canvas.style.width = this.CW + 'px';
      this.canvas.style.height = this.CH + 'px';
      this.ctx.scale(this.dpr, this.dpr);
    }

    _render() {
      const ctx = this.ctx;
      const W = this.CW;
      const H = this.CH;

      ctx.clearRect(0, 0, W, H);

      // Background — slightly lighter than pure black so frame contrast works
      ctx.fillStyle = '#1a1a1a';
      ctx.fillRect(0, 0, W, H);

      this._drawFrame(ctx, W, H);
      this.state.blocks.forEach(b => this._drawBlock(ctx, b));
      if (this.state.selected) this._drawSelection(ctx, this.state.selected);
    }

    _drawFrame(ctx, W, H) {
      const { frameStyle, frameColor } = this.state;
      const color = FRAME_COLORS[frameColor];
      const mx = 36, my = 40;
      const fw = W - mx * 2;    // ≈ 388px — comfortably over 400
      const fh = H - my * 2;
      const fx = mx, fy = my;

      ctx.save();
      ctx.shadowColor = 'rgba(0,0,0,0.55)';
      ctx.shadowBlur = 28;
      ctx.shadowOffsetY = 12;

      ctx.beginPath();
      if (frameStyle === 'arch') {
        const archH = fw * 0.26;
        const cx = fx + fw / 2;
        ctx.moveTo(fx, fy + archH);
        ctx.ellipse(cx, fy + archH, fw / 2, archH, 0, Math.PI, 0);
        ctx.lineTo(fx + fw, fy + archH);
        ctx.lineTo(fx + fw, fy + fh);
        ctx.lineTo(fx, fy + fh);
        ctx.closePath();
      } else if (frameStyle === 'moss') {
        const r = 12;
        if (ctx.roundRect) {
          ctx.roundRect(fx, fy, fw, fh, r);
        } else {
          this._roundRect(ctx, fx, fy, fw, fh, r);
        }
      } else {
        ctx.rect(fx, fy, fw, fh);
      }

      ctx.fillStyle = color;
      ctx.fill();

      // Reset shadow before border
      ctx.shadowColor = 'transparent';
      ctx.shadowBlur = 0;
      ctx.shadowOffsetY = 0;

      // Frame border
      ctx.lineWidth = 1.5;
      ctx.strokeStyle = FRAME_BORDERS[frameColor];
      ctx.stroke();

      // Moss corner decoration
      if (frameStyle === 'moss') this._drawMoss(ctx, fx, fy, fw, fh, frameColor);

      // Inner magnetic area dashed outline
      const ip = 20;
      const extraTop = frameStyle === 'arch' ? fw * 0.28 * 0.6 : 0;
      const ix = fx + ip;
      const iy = fy + ip + extraTop;
      const iw = fw - ip * 2;
      const ih = fh - ip * 2 - extraTop;

      ctx.save();
      ctx.beginPath();
      ctx.rect(ix, iy, iw, ih);
      ctx.lineWidth = 1.2;
      ctx.strokeStyle = MAG_BORDER_COLOR;
      ctx.setLineDash([4, 5]);
      ctx.stroke();
      ctx.setLineDash([]);

      // Magnetic area label — brass text
      ctx.font = '11px "Helvetica Neue", Helvetica, Arial, sans-serif';
      ctx.fillStyle = MAG_TEXT_COLOR;
      ctx.textAlign = 'center';
      ctx.letterSpacing = '0.08em';
      ctx.fillText('12 × 14"  magnetic display area', ix + iw / 2, iy + ih / 2);
      ctx.textAlign = 'left';
      ctx.restore();

      ctx.restore();
    }

    _drawMoss(ctx, fx, fy, fw, fh, colorKey) {
      const dotColor = colorKey === 'black' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)';
      ctx.fillStyle = dotColor;
      const dotR = 1.4, sp = 7, area = 32;
      for (let dx = 0; dx < area; dx += sp) {
        for (let dy = 0; dy < area; dy += sp) {
          [[fx + dx + 5, fy + dy + 5],
           [fx + fw - dx - 5, fy + dy + 5],
           [fx + dx + 5, fy + fh - dy - 5],
           [fx + fw - dx - 5, fy + fh - dy - 5]].forEach(([x, y]) => {
            ctx.beginPath();
            ctx.arc(x, y, dotR, 0, Math.PI * 2);
            ctx.fill();
          });
        }
      }
    }

    _roundRect(ctx, x, y, w, h, r) {
      ctx.moveTo(x + r, y);
      ctx.lineTo(x + w - r, y);
      ctx.quadraticCurveTo(x + w, y, x + w, y + r);
      ctx.lineTo(x + w, y + h - r);
      ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
      ctx.lineTo(x + r, y + h);
      ctx.quadraticCurveTo(x, y + h, x, y + h - r);
      ctx.lineTo(x, y + r);
      ctx.quadraticCurveTo(x, y, x + r, y);
      ctx.closePath();
    }

    _drawBlock(ctx, b) {
      if (!b.text) return;
      ctx.save();
      const font = FONT_MAP[b.font] || FONT_MAP['clean-sans'];
      ctx.font = `${b.italic ? 'italic ' : ''}${b.bold ? '700' : '400'} ${b.size}px ${font}`;
      ctx.fillStyle = b.color;
      ctx.globalAlpha = b.opacity;
      ctx.fillText(b.text, b.x, b.y);
      ctx.restore();
    }

    _drawSelection(ctx, b) {
      if (!b || !b.text) return;
      ctx.save();
      const font = FONT_MAP[b.font] || FONT_MAP['clean-sans'];
      ctx.font = `${b.italic ? 'italic ' : ''}${b.bold ? '700' : '400'} ${b.size}px ${font}`;
      const m = ctx.measureText(b.text);
      const pad = 5;
      ctx.strokeStyle = '#C8A96E';
      ctx.lineWidth = 1.2;
      ctx.setLineDash([3, 3]);
      ctx.strokeRect(b.x - pad, b.y - b.size - pad, m.width + pad * 2, b.size + pad * 2);
      ctx.setLineDash([]);
      ctx.restore();
    }

    _hitTest(x, y) {
      for (let i = this.state.blocks.length - 1; i >= 0; i--) {
        const b = this.state.blocks[i];
        if (!b.text) continue;
        this.ctx.save();
        const font = FONT_MAP[b.font] || FONT_MAP['clean-sans'];
        this.ctx.font = `${b.italic ? 'italic ' : ''}${b.bold ? '700' : '400'} ${b.size}px ${font}`;
        const m = this.ctx.measureText(b.text);
        this.ctx.restore();
        const pad = 8;
        if (x >= b.x - pad && x <= b.x + m.width + pad &&
            y >= b.y - b.size - pad && y <= b.y + pad) return b;
      }
      return null;
    }

    _bindControls() {
      const c = this.container;

      c.querySelectorAll('.cfg-frame-tile').forEach(tile => {
        tile.addEventListener('click', () => {
          c.querySelectorAll('.cfg-frame-tile').forEach(t => t.classList.remove('active'));
          tile.classList.add('active');
          this.state.frameStyle = tile.dataset.style;
          this._render();
          this._updatePrice();
        });
      });

      c.querySelectorAll('.cfg-swatch').forEach(swatch => {
        swatch.addEventListener('click', () => {
          c.querySelectorAll('.cfg-swatch').forEach(s => s.classList.remove('active'));
          swatch.classList.add('active');
          this.state.frameColor = swatch.dataset.color;
          this._render();
        });
      });

      c.querySelectorAll('.cfg-addon').forEach(addon => {
        addon.addEventListener('click', () => {
          const key = addon.dataset.addon;
          if (this.state.addons.has(key)) {
            this.state.addons.delete(key);
            addon.classList.remove('active');
          } else {
            this.state.addons.add(key);
            addon.classList.add('active');
          }
          this._updatePrice();
        });
      });

      const addBtn = c.querySelector('.cfg-add-text-btn');
      if (addBtn) addBtn.addEventListener('click', () => this._addBlock());

      const textarea = c.querySelector('.cfg-textarea');
      if (textarea) textarea.addEventListener('input', () => {
        if (!this.state.selected) return;
        this.state.selected.text = textarea.value;
        this._render();
        this._syncXY();
      });

      const fontSel = c.querySelector('.cfg-font-select');
      if (fontSel) fontSel.addEventListener('change', () => {
        if (!this.state.selected) return;
        this.state.selected.font = fontSel.value;
        this._render();
      });

      const sizeSl = c.querySelector('.cfg-size-slider');
      const sizeVal = c.querySelector('.cfg-size-val');
      if (sizeSl) sizeSl.addEventListener('input', () => {
        const v = parseInt(sizeSl.value);
        if (sizeVal) sizeVal.textContent = v;
        if (!this.state.selected) return;
        this.state.selected.size = v;
        this._render();
        this._syncXY();
      });

      c.querySelectorAll('.cfg-text-color').forEach(sw => {
        sw.addEventListener('click', () => {
          c.querySelectorAll('.cfg-text-color').forEach(s => s.classList.remove('active'));
          sw.classList.add('active');
          if (!this.state.selected) return;
          this.state.selected.color = sw.dataset.color;
          this._render();
        });
      });

      const boldBtn = c.querySelector('.cfg-bold-btn');
      if (boldBtn) boldBtn.addEventListener('click', () => {
        if (!this.state.selected) return;
        this.state.selected.bold = !this.state.selected.bold;
        boldBtn.classList.toggle('active', this.state.selected.bold);
        this._render();
      });

      const italicBtn = c.querySelector('.cfg-italic-btn');
      if (italicBtn) italicBtn.addEventListener('click', () => {
        if (!this.state.selected) return;
        this.state.selected.italic = !this.state.selected.italic;
        italicBtn.classList.toggle('active', this.state.selected.italic);
        this._render();
      });

      const opacitySl = c.querySelector('.cfg-opacity-slider');
      const opacityVal = c.querySelector('.cfg-opacity-val');
      if (opacitySl) opacitySl.addEventListener('input', () => {
        const v = parseFloat(opacitySl.value);
        if (opacityVal) opacityVal.textContent = Math.round(v * 100) + '%';
        if (!this.state.selected) return;
        this.state.selected.opacity = v;
        this._render();
      });

      const xIn = c.querySelector('.cfg-x-input');
      const yIn = c.querySelector('.cfg-y-input');
      if (xIn) xIn.addEventListener('change', () => {
        if (!this.state.selected) return;
        this.state.selected.x = parseInt(xIn.value) || this.state.selected.x;
        this._render();
      });
      if (yIn) yIn.addEventListener('change', () => {
        if (!this.state.selected) return;
        this.state.selected.y = parseInt(yIn.value) || this.state.selected.y;
        this._render();
      });
    }

    _bindCanvas() {
      const canvas = this.canvas;

      canvas.addEventListener('mousedown', e => {
        const pos = this._canvasPos(e);
        const hit = this._hitTest(pos.x, pos.y);
        if (hit) {
          this.state.selected = hit;
          this.state.dragging = true;
          this.state.dragOX = pos.x - hit.x;
          this.state.dragOY = pos.y - hit.y;
          canvas.style.cursor = 'grabbing';
          this._syncPanel(hit);
        } else {
          this.state.selected = null;
          canvas.style.cursor = 'default';
        }
        this._render();
      });

      canvas.addEventListener('mousemove', e => {
        const pos = this._canvasPos(e);
        if (this.state.dragging && this.state.selected) {
          this.state.selected.x = pos.x - this.state.dragOX;
          this.state.selected.y = pos.y - this.state.dragOY;
          this._render();
          this._syncXY();
        } else {
          canvas.style.cursor = this._hitTest(pos.x, pos.y) ? 'grab' : 'default';
        }
      });

      canvas.addEventListener('mouseup', () => {
        this.state.dragging = false;
        if (this.state.selected) canvas.style.cursor = 'grab';
      });
      canvas.addEventListener('mouseleave', () => { this.state.dragging = false; });

      canvas.addEventListener('touchstart', e => {
        e.preventDefault();
        const pos = this._touchPos(e.touches[0]);
        const hit = this._hitTest(pos.x, pos.y);
        if (hit) {
          this.state.selected = hit;
          this.state.dragging = true;
          this.state.dragOX = pos.x - hit.x;
          this.state.dragOY = pos.y - hit.y;
          this._syncPanel(hit);
          this._render();
        }
      }, { passive: false });

      canvas.addEventListener('touchmove', e => {
        e.preventDefault();
        if (!this.state.dragging || !this.state.selected) return;
        const pos = this._touchPos(e.touches[0]);
        this.state.selected.x = pos.x - this.state.dragOX;
        this.state.selected.y = pos.y - this.state.dragOY;
        this._render();
        this._syncXY();
      }, { passive: false });

      canvas.addEventListener('touchend', () => { this.state.dragging = false; });
    }

    _canvasPos(e) {
      const rect = this.canvas.getBoundingClientRect();
      return {
        x: (e.clientX - rect.left) * (this.CW / rect.width),
        y: (e.clientY - rect.top) * (this.CH / rect.height),
      };
    }

    _touchPos(touch) {
      const rect = this.canvas.getBoundingClientRect();
      return {
        x: (touch.clientX - rect.left) * (this.CW / rect.width),
        y: (touch.clientY - rect.top) * (this.CH / rect.height),
      };
    }

    _addBlock() {
      const block = {
        id: this.nextId++,
        text: 'Your Text',
        font: 'clean-sans',
        size: 24,
        color: '#FFFFFF',
        bold: false,
        italic: false,
        opacity: 1,
        x: this.CW / 2 - 50,
        y: this.CH / 2,
      };
      this.state.blocks.push(block);
      this.state.selected = block;
      this._render();
      this._syncPanel(block);
    }

    _syncPanel(b) {
      if (!b) return;
      const c = this.container;
      const get = sel => c.querySelector(sel);

      const ta = get('.cfg-textarea');
      if (ta) ta.value = b.text;

      const fs = get('.cfg-font-select');
      if (fs) fs.value = b.font;

      const ssl = get('.cfg-size-slider');
      const sv = get('.cfg-size-val');
      if (ssl) { ssl.value = b.size; if (sv) sv.textContent = b.size; }

      const boldBtn = get('.cfg-bold-btn');
      if (boldBtn) boldBtn.classList.toggle('active', b.bold);

      const italicBtn = get('.cfg-italic-btn');
      if (italicBtn) italicBtn.classList.toggle('active', b.italic);

      const osl = get('.cfg-opacity-slider');
      const ov = get('.cfg-opacity-val');
      if (osl) { osl.value = b.opacity; if (ov) ov.textContent = Math.round(b.opacity * 100) + '%'; }

      c.querySelectorAll('.cfg-text-color').forEach(sw => {
        sw.classList.toggle('active', sw.dataset.color === b.color);
      });

      this._syncXY();
    }

    _syncXY() {
      if (!this.state.selected) return;
      const c = this.container;
      const xi = c.querySelector('.cfg-x-input');
      const yi = c.querySelector('.cfg-y-input');
      if (xi) xi.value = Math.round(this.state.selected.x);
      if (yi) yi.value = Math.round(this.state.selected.y);
    }

    _calcPrice() {
      let p = PRICES.base;
      if (this.state.frameStyle === 'arch') p += PRICES.arch;
      if (this.state.frameStyle === 'moss') p += PRICES.moss;
      this.state.addons.forEach(k => { p += PRICES[k] || 0; });
      return p;
    }

    _updatePrice() {
      const el = this.container.querySelector('.cfg-price-amount');
      if (el) el.textContent = '$' + this._calcPrice();
    }

    _buildTextColorSwatches() {
      const wrap = this.container.querySelector('.cfg-text-colors');
      if (!wrap) return;
      wrap.innerHTML = '';
      TEXT_COLORS.forEach((color, i) => {
        const sw = document.createElement('div');
        sw.className = 'cfg-text-color' + (i === 0 ? ' active' : '');
        sw.dataset.color = color;
        sw.style.backgroundColor = color;
        if (color === '#FFFFFF') sw.style.border = '1.5px solid rgba(255,255,255,0.25)';
        wrap.appendChild(sw);
      });
    }
  }

  function init() {
    document.querySelectorAll('[data-ssmade-configurator]').forEach(el => {
      if (el._cfgInited) return;
      el._cfgInited = true;
      new SSMadeConfigurator(el);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', init);
  window.SSMadeConfigurator = SSMadeConfigurator;

})();
